import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { ApprovalFlowHeaderDisplay } from '@/accessControl/ApprovalFlowHeaderDisplay.molecule';
import { ApprovalFlowModal } from '@/accessControl/ApprovalFlowModal.molecule';
import { ApprovalStepModal } from '@/accessControl/ApprovalStepModal';
import { useApprovalFlowHeaderController } from '@/accessControl/hooks/useApprovalFlowHeaderController.hook';
import { WorkbookActionsMenu } from '@/header/WorkbookActionsMenu.molecule';
import { headerStyles as styles } from '@/header/header.styles';
import { useDuplicateWorkbook } from '@/header/useDuplicateWorkbook.hook';
import { useWorkbookHeaderFocus } from '@/header/useWorkbookHeaderFocus.hook';
import { SeeqNames } from '@seeqdev/constants';
import type { DropdownItems } from '@seeqdev/qomponents';

/** Shows workbook actions, approval status, and dialogs beside the title. */
export const WorkbookHeaderControls = () => {
  const { t } = useTranslation();
  const { workbookType, canDuplicate, isDuplicating, duplicateWorkbook } = useDuplicateWorkbook();
  const { menuRef, statusRef, requestAttachmentFocus, resetAttachmentFocus, focusMenu, focusStatus } =
    useWorkbookHeaderFocus();
  const approval = useApprovalFlowHeaderController({
    onOpenApply: resetAttachmentFocus,
    onAttachmentComplete: requestAttachmentFocus,
  });
  const { nextStep } = approval;
  const showApprovalControls = approval.approvalFlowsEnabled && (approval.showApplyBtn || approval.showStepDropdown);
  const showApplyMenu = showApprovalControls && approval.showApplyBtn && !approval.isInProd && !approval.approved;
  const showStatus = showApprovalControls && (approval.isInProd || approval.approved || approval.showStepDropdown);
  const items: DropdownItems[] = showApplyMenu
    ? [{ label: t('APPROVAL_FLOWS.APPLY_TITLE'), disabled: approval.isAttaching, onClick: approval.openApply }]
    : [];
  if (canDuplicate) {
    items.push({
      label: t(
        workbookType === SeeqNames.Types.Topic
          ? 'WORKBENCH.DUPLICATE_TOPIC'
          : workbookType === SeeqNames.Types.ActionBoard
            ? 'WORKBENCH.DUPLICATE_BOARD'
            : 'WORKBENCH.DUPLICATE_ANALYSIS',
      ),
      disabled: isDuplicating,
      onClick: () => void duplicateWorkbook(),
    });
  }

  return (
    <>
      <div {...stylex.props(styles.workbookControls)}>
        {items.length > 0 ? (
          <span ref={menuRef} {...stylex.props(styles.workbookMenu)}>
            <WorkbookActionsMenu
              items={items}
              accessibleName={t('WORKBOOK_ACTIONS')}
              isPending={approval.isAttaching || isDuplicating}
              setFocusOnTriggerOnClose={approval.activeModal !== 'FLOW_MODAL'}
            />
          </span>
        ) : null}
        {showStatus ? (
          <span ref={statusRef} tabIndex={-1} {...stylex.props(styles.approvalStatus)}>
            <ApprovalFlowHeaderDisplay
              isInProd={approval.isInProd}
              approved={approval.approved}
              isResolvingProductionStatus={approval.isResolvingProductionStatus}
              pendingStepName={nextStep?.name}
              currentStepNumber={nextStep?.position}
              totalSteps={approval.approvalFlow?.steps.length}
              canApprove={Boolean(nextStep?.permissions.write)}
              isDialogOpen={approval.activeModal !== null}
              onApprove={approval.openApprove}
              onViewSteps={approval.openViewSteps}
            />
          </span>
        ) : null}
      </div>
      {showApprovalControls && approval.activeModal === 'FLOW_MODAL' ? (
        <ApprovalFlowModal
          isApplyingFlow={approval.showApplyBtn}
          approvalFlow={approval.approvalFlow}
          currentStepId={approval.showApplyBtn ? undefined : nextStep?.id}
          itemType={approval.itemType}
          isApplying={approval.isAttaching}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (showApplyMenu) {
              if (!approval.didAttachFlow()) {
                focusMenu();
              }
            } else {
              focusStatus();
            }
          }}
          onSubmit={approval.showApplyBtn ? approval.applyApprovalFlow : async () => approval.closeModal()}
          onClose={approval.closeModal}
        />
      ) : null}
      {showApprovalControls && approval.activeModal === 'STEP_MODAL' && nextStep ? (
        <ApprovalStepModal
          step={nextStep}
          futureStep={approval.futureStep}
          approvalFlow={approval.approvalFlow}
          itemType={approval.itemType}
          isSubmitting={approval.isAssigning}
          hasLockingFailedPreviouslyDueToGlobalItems={approval.hasLockingFailedPreviouslyDueToGlobalItems}
          onSubmit={(versionState) => approval.approveStep(nextStep, versionState)}
          onClose={approval.closeModal}
          isItemLocked={approval.isLocked}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            focusStatus();
          }}
        />
      ) : null}
    </>
  );
};
